import { useEffect, useState } from "react";
import { loginWithPin } from "@/lib/api/auth";
import { ApiError } from "@/lib/api/client";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PinInput } from "@/components/auth/pin-input";

function initialsFor(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return parts
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");
}

function formatCountdown(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

// PIN lockouts are 15 minutes server-side; used when a 429 arrives without retryAfterSeconds.
const DEFAULT_LOCK_SECONDS = 15 * 60;

/**
 * PIN prompt for switching to `profile` ({userId, loginId, displayName}). Wrong PIN (401)
 * clears + shakes; lockout (429) disables the input for the server's retryAfterSeconds;
 * anything else (old backend without the endpoint, network trouble) degrades to the password
 * path via onUsePassword. A failed attempt never touches the currently active session.
 */
export function PinEntryDialog({ open, onOpenChange, profile, onSuccess, onUsePassword }) {
  const [pin, setPin] = useState("");
  const [errorText, setErrorText] = useState("");
  const [shakeSignal, setShakeSignal] = useState(0);
  const [lockSecondsLeft, setLockSecondsLeft] = useState(0);
  const [unavailable, setUnavailable] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setPin("");
    setErrorText("");
    setLockSecondsLeft(0);
    setUnavailable(false);
    setIsSubmitting(false);
  }, [open, profile?.userId]);

  useEffect(() => {
    if (lockSecondsLeft <= 0) return undefined;
    const timer = setInterval(() => {
      setLockSecondsLeft((left) => (left > 1 ? left - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [lockSecondsLeft > 0]);

  const handleSubmit = async (submittedPin) => {
    if (isSubmitting || lockSecondsLeft > 0 || unavailable || !profile?.loginId) return;
    setIsSubmitting(true);
    setErrorText("");
    try {
      const result = await loginWithPin({ loginId: profile.loginId, pin: submittedPin });
      onOpenChange?.(false);
      onSuccess?.(result);
    } catch (error) {
      setPin("");
      if (error instanceof ApiError && error.status === 401) {
        setErrorText("Wrong PIN — try again.");
        setShakeSignal((n) => n + 1);
      } else if (error instanceof ApiError && error.status === 429) {
        const retryAfter = Number(error.payload?.retryAfterSeconds);
        setLockSecondsLeft(
          Number.isFinite(retryAfter) && retryAfter > 0 ? Math.ceil(retryAfter) : DEFAULT_LOCK_SECONDS,
        );
      } else if (error instanceof ApiError && [404, 405, 501].includes(error.status)) {
        // Old backend without /api/login/pin — the endpoint genuinely doesn't exist.
        setUnavailable(true);
      } else {
        // Transient server/network trouble: keep the PIN input usable so a retry works —
        // flipping to "unavailable" here would hide it for the rest of the dialog session.
        console.error("[Auth] PIN login failed:", error);
        setErrorText("Something went wrong. Try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const locked = lockSecondsLeft > 0;
  const displayName = String(profile?.displayName || profile?.loginId || "").trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarFallback className="text-sm font-semibold">{initialsFor(displayName)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <DialogTitle className="truncate">{displayName}</DialogTitle>
              <DialogDescription className="truncate">{profile?.loginId || ""}</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3 py-2">
          {unavailable ? (
            <p className="text-center text-sm text-muted-foreground">
              PIN sign-in is unavailable right now. Use your password instead.
            </p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">Enter your PIN to switch</p>
              <PinInput
                value={pin}
                onChange={(next) => {
                  setPin(next);
                  if (errorText) setErrorText("");
                }}
                onSubmit={(next) => void handleSubmit(next)}
                disabled={isSubmitting || locked}
                autoFocus
                error={Boolean(errorText)}
                shakeSignal={shakeSignal}
              />
              {errorText ? <p className="text-sm text-destructive">{errorText}</p> : null}
              {locked ? (
                <p className="text-sm text-destructive">
                  Too many attempts. Try again in {formatCountdown(lockSecondsLeft)}.
                </p>
              ) : null}
            </>
          )}
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            onClick={() => {
              onOpenChange?.(false);
              onUsePassword?.(profile);
            }}
          >
            Use password instead
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
